import { apiRoute, PRODUCT } from "./config";

export interface WebSession {
  authenticated: boolean;
  deviceID?: string;
  deviceName?: string;
  sessionType?: "account";
}

export interface AccountSession {
  displayName: string;
  enrolledAt: string;
  lastSeenAt: string | null;
  current: boolean;
}

export interface AccountDeletionRequest {
  status: "pending";
  requestedAt: string;
  sessionsRevoked: true;
  fulfillment: "manual_review";
}

export type MagicLinkRequestState = "sent" | "rate_limited";

export const WEB_AUTH_MESSAGES = {
  sessionUnavailable:
    "We couldn’t reconnect this browser to your private space. Try again in a moment.",
  magicLinkSent:
    "Email sent. Check your inbox to log in or sign up.",
  magicLinkRateLimited:
    "Please wait a minute before requesting another sign-in link.",
  magicLinkRequestUnavailable:
    "We couldn’t send a sign-in link. Try again in a moment.",
  magicLinkUnavailable:
    "That sign-in link is no longer available.",
  sessionsUnavailable:
    "Your signed-in devices couldn’t be loaded. Try again in a moment.",
  logoutUnavailable:
    "This browser couldn’t be signed out. Try again in a moment.",
  signOutEverywhereUnavailable:
    "Your devices couldn’t be signed out. Try again in a moment.",
  deletionUnavailable:
    "Your deletion request couldn’t be started. Try again in a moment.",
} as const;

interface WebSessionPayload {
  authenticated?: unknown;
  device_id?: unknown;
  device_name?: unknown;
  session_type?: unknown;
}

interface AccountSessionPayload {
  id?: unknown;
  display_name?: unknown;
  enrolled_at?: unknown;
  last_seen_at?: unknown;
  current?: unknown;
}

export interface MagicLinkLocation {
  hash: string;
  pathname: string;
  search: string;
}

export interface MagicLinkHistory {
  readonly state: unknown;
  replaceState(data: unknown, unused: string, url?: string | URL | null): void;
}

async function jsonObject(response: Response): Promise<Record<string, unknown>> {
  const contentType = response.headers.get("content-type")?.toLowerCase() ?? "";
  if (!contentType.includes("application/json")) {
    throw new Error("unexpected_response");
  }
  const value: unknown = await response.json();
  if (!value || Array.isArray(value) || typeof value !== "object") {
    throw new Error("unexpected_response");
  }
  return value as Record<string, unknown>;
}

function normalizedSessionPayload(payload: WebSessionPayload): WebSession {
  if (payload.authenticated === false) {
    return { authenticated: false };
  }
  if (
    payload.authenticated !== true
    || typeof payload.device_id !== "string"
    || payload.device_id.length < 1
    || typeof payload.device_name !== "string"
    || payload.device_name.length < 1
    || (
      payload.session_type !== "account"
      && payload.session_type !== "private-beta-device"
    )
  ) {
    throw new Error("unexpected_response");
  }
  return {
    authenticated: true,
    deviceID: payload.device_id,
    deviceName: payload.device_name,
    sessionType: "account",
  };
}

async function sessionPayload(response: Response): Promise<WebSession> {
  return normalizedSessionPayload(
    await jsonObject(response) as WebSessionPayload,
  );
}

function accountSession(payload: AccountSessionPayload): AccountSession {
  if (
    typeof payload.id !== "string"
    || payload.id.length < 1
    || typeof payload.display_name !== "string"
    || payload.display_name.length < 1
    || typeof payload.enrolled_at !== "string"
    || !Number.isFinite(Date.parse(payload.enrolled_at))
    || (
      payload.last_seen_at !== null
      && (
        typeof payload.last_seen_at !== "string"
        || !Number.isFinite(Date.parse(payload.last_seen_at))
      )
    )
    || typeof payload.current !== "boolean"
  ) {
    throw new Error("unexpected_response");
  }
  return {
    displayName: payload.display_name,
    enrolledAt: payload.enrolled_at,
    lastSeenAt: payload.last_seen_at,
    current: payload.current,
  };
}

export function consumeMagicLinkFragment(
  location: MagicLinkLocation,
  history: MagicLinkHistory,
): string | null {
  const fragment = location.hash.startsWith("#")
    ? location.hash.slice(1)
    : location.hash;
  history.replaceState(
    history.state,
    "",
    `${location.pathname}${location.search}`,
  );
  const parameters = new URLSearchParams(fragment);
  const tokens = parameters.getAll("token");
  if (
    tokens.length !== 1
    || Array.from(parameters.keys()).some((key) => key !== "token")
    || !/^[A-Za-z0-9_-]{43}$/.test(tokens[0])
  ) {
    return null;
  }
  return tokens[0];
}

export class CloudflareWebAuth {
  async session(): Promise<WebSession> {
    try {
      const response = await fetch(apiRoute("/web/session"), {
        credentials: "same-origin",
        cache: "no-store",
      });
      if (response.status === 401 || response.status === 404) {
        return { authenticated: false };
      }
      if (!response.ok) {
        throw new Error("session_unavailable");
      }
      return await sessionPayload(response);
    } catch {
      throw new Error(WEB_AUTH_MESSAGES.sessionUnavailable);
    }
  }

  async requestMagicLink(email: string): Promise<MagicLinkRequestState> {
    try {
      const response = await fetch(apiRoute("/auth/magic-link/request"), {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, client: "web" }),
      });
      const payload = await jsonObject(response);
      if (response.status === 202 && payload.accepted === true) {
        return "sent";
      }
      if (
        response.status === 429
        && payload.accepted === false
        && payload.code === "try_again_later"
      ) {
        return "rate_limited";
      }
      throw new Error("request_unavailable");
    } catch {
      throw new Error(WEB_AUTH_MESSAGES.magicLinkRequestUnavailable);
    }
  }

  async exchangeMagicLink(token: string): Promise<WebSession> {
    try {
      const response = await fetch(apiRoute("/auth/magic-link/exchange"), {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          client: "web",
          token,
          device_name: PRODUCT.webDeviceLabel,
        }),
      });
      if (!response.ok) {
        throw new Error("link_unavailable");
      }
      return normalizedSessionPayload(
        await jsonObject(response) as WebSessionPayload,
      );
    } catch {
      throw new Error(WEB_AUTH_MESSAGES.magicLinkUnavailable);
    }
  }

  async sessions(): Promise<AccountSession[]> {
    try {
      const response = await fetch(apiRoute("/account/sessions"), {
        credentials: "same-origin",
        cache: "no-store",
      });
      if (!response.ok) throw new Error("sessions_unavailable");
      const payload = await jsonObject(response);
      if (!Array.isArray(payload.sessions)) {
        throw new Error("unexpected_response");
      }
      const sessions = payload.sessions.map((session) => {
        if (!session || Array.isArray(session) || typeof session !== "object") {
          throw new Error("unexpected_response");
        }
        return accountSession(session as AccountSessionPayload);
      });
      if (
        sessions.length < 1
        || sessions.filter(({ current }) => current).length !== 1
      ) {
        throw new Error("unexpected_response");
      }
      return sessions;
    } catch {
      throw new Error(WEB_AUTH_MESSAGES.sessionsUnavailable);
    }
  }

  async logout(): Promise<void> {
    try {
      const response = await fetch(apiRoute("/account/logout"), {
        method: "POST",
        credentials: "same-origin",
      });
      if (!response.ok) throw new Error("logout_unavailable");
      const payload = await jsonObject(response);
      if (payload.logged_out !== true) {
        throw new Error("unexpected_response");
      }
    } catch {
      throw new Error(WEB_AUTH_MESSAGES.logoutUnavailable);
    }
  }

  async signOutEverywhere(): Promise<number> {
    try {
      const response = await fetch(
        apiRoute("/account/sessions/revoke-all"),
        {
          method: "POST",
          credentials: "same-origin",
        },
      );
      if (!response.ok) throw new Error("sign_out_unavailable");
      const payload = await jsonObject(response);
      if (
        payload.signed_out_everywhere !== true
        || typeof payload.revoked_sessions !== "number"
        || !Number.isSafeInteger(payload.revoked_sessions)
        || payload.revoked_sessions < 1
      ) {
        throw new Error("unexpected_response");
      }
      return payload.revoked_sessions;
    } catch {
      throw new Error(WEB_AUTH_MESSAGES.signOutEverywhereUnavailable);
    }
  }

  async requestDeletion(): Promise<AccountDeletionRequest> {
    try {
      const response = await fetch(
        apiRoute("/account/deletion-requests"),
        {
          method: "POST",
          credentials: "same-origin",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ confirm: "delete my account" }),
        },
      );
      if (response.status !== 202) throw new Error("deletion_unavailable");
      const payload = await jsonObject(response);
      if (
        payload.status !== "pending"
        || typeof payload.requested_at !== "string"
        || !Number.isFinite(Date.parse(payload.requested_at))
        || payload.sessions_revoked !== true
        || payload.fulfillment !== "manual_review"
      ) {
        throw new Error("unexpected_response");
      }
      return {
        status: "pending",
        requestedAt: payload.requested_at,
        sessionsRevoked: true,
        fulfillment: "manual_review",
      };
    } catch {
      throw new Error(WEB_AUTH_MESSAGES.deletionUnavailable);
    }
  }
}
